// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useEffect, useCallback, useActionState, useMemo, useLayoutEffect } from 'react';

import type {
  WorkOrderId,
  WorkOrderItemId,
  TaskId,
  EntityAttachmentType,
  UploadableEntityAttachmentType,
  EntityAttachment,
  FileId,
  TaskTypeId,
  Project,
  SpoolWithModel,
  StrongExtract,
} from '@evolvemep/api-ts';
import { Stack, LoadingOverlay } from '@mantine/core';

import { AttachmentSection } from 'components/Mantine/Attachments/AttachmentSection';
import { hasLengthAtLeast } from 'helpers/helper-types';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { strongIsEqual, strongEntries, strongValues } from 'helpers/strongEntries';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { type AccessArea, useGetHasAccess } from 'hooks/user/useHasAccess';
import { FullScreenAttachmentViewer } from 'modules/Shop/Tasks/TaskViewer/FullScreenAttachmentViewer';
import { ModelSelector } from 'modules/Shop/Tasks/TaskViewer/ModelSelector';

export type AttachmentListProps = {
  workOrderId?: WorkOrderId;
  workOrderItemId?: WorkOrderItemId;
  taskId?: TaskId;
  taskTypeId?: TaskTypeId;

  /** If provided, will display the Spool as a possible attachment. */
  spool?: SpoolWithModel;
  /** If provided, will display all models in this project as possible attachments. */
  project?: Pick<Project, 'projectId' | 'projectName'>;
  loading?: boolean;

  /** Hide particular sections, even if a matching `id` was provided. */
  excludeSections?: EntityAttachmentType[];
  /** Which entity type to allow uploads for, if any. */
  allowUploads?: UploadableEntityAttachmentType;
  /** Called after files have been successfully uploaded. */
  onFilesUploaded?: (fileIds: FileId[]) => void;
  /** Called after a web link has been successfully added. */
  onWebLinkAdded?: () => void;
  /** Called after an attachment has been successfully deleted. */
  onAttachmentDeleted?: () => void;
  /** Called when the total number of attachments has been loaded. */
  onAttachmentCountRealized?: (count: number) => void;
  /** If true, will automatically load the next page of attachments when the user scrolls to the bottom. */
  autoloadRemainingAttachments?: boolean;
  /** Per-section refresh nonces; bump a section's value to force that section to refetch in place. */
  refreshTriggers?: Partial<Record<EntityAttachmentType, number>>;
  /**
   * Called when an attachment is selected.
   *
   * If not provided, attachments will automatically download on click.
   */
  onAttachmentSelected?: (attachment: EntityAttachment<never>, rowIndex: number) => void;

  /**
   * If provided, will be called with data[selectedRow]
   * of the specific `selectedSection`, assuming that section has data.
   */
  onRowRealized?: (attachment: EntityAttachment<never>, rowIndex: number) => void;
  /** Used with `onRowRealized` */
  selectedRow?: number;
  /**
   * If true, will open the full-screen attachment viewer upon clicking a compatible attachment.
   *
   * @defaultValue true
   */
  openFullScreenViewer?: boolean;
  /** For pagination */
  pageSize?: number;
};

const defaultSectionOrder = [
  'task',
  'workOrderBop',
  'workOrderItems',
  'workOrder',
  'workOrderItem',
  'taskType',
] as const satisfies EntityAttachmentType[];

/** Defines the access needed to read these attachments */
const sectionAccess = {
  task: 'tasks',
  workOrderBop: 'tasks',
  workOrderItems: 'workOrders',
  workOrder: 'workOrders',
  workOrderItem: 'workOrders',
  taskType: 'workCellSetup',
} as const satisfies {
  /**
   * Typed as such to make sure {@link defaultSectionOrder} defines every `EntityAttachmentType`
   * (truly just reusing `sectionAccess` as a type-check for `defaultSectionOrder`
   */
  [K in (typeof defaultSectionOrder)[keyof typeof defaultSectionOrder] & EntityAttachmentType]: AccessArea;
};

const entityIdProp = {
  task: 'taskId',
  taskType: 'taskTypeId',
  workOrderItem: 'workOrderItemId',
  workOrder: 'workOrderId',
  workOrderBop: 'workOrderId',
  workOrderItems: 'workOrderId',
} as const satisfies Record<
  EntityAttachmentType,
  StrongExtract<keyof AttachmentListProps, 'workOrderId' | 'workOrderItemId' | 'taskId' | 'taskTypeId'>
>;
const entityIds = strongValues(entityIdProp);

const getEntityId = (
  entityType: EntityAttachmentType,
  props: Pick<AttachmentListProps, (typeof entityIdProp)[keyof typeof entityIdProp]>,
) => props[entityIdProp[entityType]];

declare const viewerOwnerKeySymbol: unique symbol;
/** Identifies which `AttachmentList` instance owns the persisted `VIEWING_ATTACHMENT` value. */
export type ViewerOwnerKey = string & { [viewerOwnerKeySymbol]: never };
const getViewerOwnerKey = (props: Pick<AttachmentListProps, (typeof entityIdProp)[keyof typeof entityIdProp]>) =>
  strongEntries(props)
    .filter(([key]) => entityIds.includes(key))
    .map(([, value]) => value)
    .filter(isNotNil)
    .join('|') as ViewerOwnerKey;

/** Get the section order, filtering out any excluded sections or ones not passed in */
const generateSectionOrder = (
  props: Pick<AttachmentListProps, 'excludeSections' | (typeof entityIdProp)[keyof typeof entityIdProp]>,
) =>
  (defaultSectionOrder as EntityAttachmentType[]).filter(
    (s) => !props.excludeSections?.includes(s) && isNotNil(getEntityId(s, props)),
  );

/** Used to render attachments for a set of entities. */
export const AttachmentList = ({
  onAttachmentSelected,
  onAttachmentCountRealized,
  allowUploads,
  onFilesUploaded,
  onWebLinkAdded,
  onAttachmentDeleted,
  selectedRow,
  onRowRealized: onRowRealizedParent,
  autoloadRemainingAttachments,
  openFullScreenViewer = true,
  pageSize,
  refreshTriggers,
  ...props
}: AttachmentListProps) => {
  const { hasAccess } = useGetHasAccess();
  const [sectionOrder, setSectionOrder] = useState<EntityAttachmentType[]>([]);

  useLayoutEffect(() => {
    setSectionOrder((prevOrder) => {
      const newSectionOrder = generateSectionOrder(props).filter((so) => hasAccess(sectionAccess[so], 'read'));
      // Check isEqual to prevent unnecessary re-renders
      if (!strongIsEqual(prevOrder, newSectionOrder)) return newSectionOrder;
      return prevOrder;
    });
  }, [props, hasAccess]);

  const [totalAttachmentCounts, setTotalAttachmentCount] = useActionState<
    Partial<Record<EntityAttachmentType, number | undefined>>,
    {
      count: number;
      entityType: EntityAttachmentType;
    }
  >(
    (counts, { count, entityType }) => ({
      ...counts,
      [entityType]: count,
    }),
    // Only set when mounting; any change to `sectionOrder` after mounting
    // will not properly be handled by `totalAttachmentCounts`
    sectionOrder.reduce((out, curr) => ({ ...out, [curr]: undefined }), {}),
  );

  const allAttachmentCountsRealized = useMemo(
    () => Object.values(totalAttachmentCounts).every(isNotNil),
    [totalAttachmentCounts],
  );

  useEffect(() => {
    const totals = Object.values(totalAttachmentCounts);
    if (allAttachmentCountsRealized) {
      onAttachmentCountRealized?.(totals.filter(isNotNil).reduce((out, curr) => out + curr, 0));
    }
  }, [allAttachmentCountsRealized, onAttachmentCountRealized, totalAttachmentCounts]);

  const sortedCounts = useMemo(
    () =>
      strongEntries(totalAttachmentCounts)
        .filter((c): c is [EntityAttachmentType, number] => isNotNil(c[1]))
        .toSorted(([a], [b]) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b)),
    [sectionOrder, totalAttachmentCounts],
  );

  const [selectedSection, internalRow] = useMemo(() => {
    if (!allAttachmentCountsRealized || selectedRow === undefined) return [undefined, undefined];
    const total = sortedCounts.reduce((out, [_, c]) => out + c, 0);
    if (!hasLengthAtLeast(sortedCounts, 1) || total === 0) return [undefined, undefined];

    let row = selectedRow;
    while (row < 0) row += total;
    while (row >= total) row -= total;

    let section = sortedCounts[0][0];

    for (const [s, c = 0] of sortedCounts) {
      section = s;
      if (row < c) break;

      row -= c;
    }
    return [section, row];
  }, [allAttachmentCountsRealized, selectedRow, sortedCounts]);

  /**
   * Calculates the "overall" (external) row number
   *
   * For example, if the sections are
   *
   * Tasks (5 attachments)
   * Work Orders (3 attachments)
   *
   * then `getExternalRow('workOrder', 0)` will return 5.
   */
  const getExternalRow = useCallback(
    (section: EntityAttachmentType, rowNum: number) => {
      const i = sortedCounts.findIndex(([s]) => s === section);
      if (i === -1) return rowNum;
      return sortedCounts.slice(0, i).reduce((out, [, c]) => out + c, 0) + rowNum;
    },
    [sortedCounts],
  );

  const onRowRealized = useCallback(
    <T extends EntityAttachmentType>(attachment: EntityAttachment<T> | null, rowNum: number, section: T) => {
      if (isNotNil(attachment)) {
        onRowRealizedParent?.(attachment, getExternalRow(section, rowNum));
      }
    },
    [getExternalRow, onRowRealizedParent],
  );

  const [attachmentViewerOpenedWith, setAttachmentViewerOpenedWith] = useLocalStorage('VIEWING_ATTACHMENT', undefined);

  const viewerOwner = useMemo(() => getViewerOwnerKey(props), [props]);
  // Only open our viewer for a value we wrote; other mounted AttachmentLists share this storage key.
  // Legacy persisted values may lack `owner` — the strict equality intentionally treats them as not ours.
  const viewerSelection = attachmentViewerOpenedWith?.owner === viewerOwner ? attachmentViewerOpenedWith : undefined;

  return (
    <Stack pos="relative">
      {isNotNil(props.project) && (
        <ModelSelector
          spool={props.spool}
          loadingSpool={props.loading}
          project={props.project}
          onUrnSelected={(urn, isSpoolModel = false) =>
            setAttachmentViewerOpenedWith({ urn, isSpoolModel, owner: viewerOwner })
          }
        />
      )}

      {sectionOrder.map((section) => {
        const entityId = getEntityId(section, props);
        if (isNil(entityId)) return null;
        return (
          <AttachmentSection
            key={section}
            entityType={section}
            entityId={entityId}
            active={allowUploads === section}
            onFilesUploaded={onFilesUploaded}
            onWebLinkAdded={onWebLinkAdded}
            onAttachmentDeleted={onAttachmentDeleted}
            onAttachmentSelected={
              onAttachmentSelected
                ? (attachment, rowNum) => onAttachmentSelected(attachment, getExternalRow(section, rowNum))
                : openFullScreenViewer
                  ? (attachment, rowNum) =>
                      setAttachmentViewerOpenedWith({
                        attachment,
                        rowNum: getExternalRow(section, rowNum),
                        owner: viewerOwner,
                      })
                  : undefined
            }
            onAttachmentCountRealized={setTotalAttachmentCount}
            autoloadRemainingAttachments={autoloadRemainingAttachments}
            titleSuffix={sectionOrder.length === 1 ? 'Attachments' : undefined}
            pageSize={pageSize}
            refreshTrigger={refreshTriggers?.[section]}
            {...(selectedSection === section && isNotNil(onRowRealizedParent)
              ? {
                  selectedRow: internalRow,
                  onRowRealized,
                }
              : {})}
          />
        );
      })}
      {isNil(onAttachmentSelected) && openFullScreenViewer && (
        <FullScreenAttachmentViewer
          opened={isNotNil(viewerSelection)}
          onClose={() => setAttachmentViewerOpenedWith(undefined)}
          defaultSelection={viewerSelection}
          owner={viewerOwner}
          {...props}
        />
      )}
      <LoadingOverlay visible={!allAttachmentCountsRealized} />
    </Stack>
  );
};
